import { getDatabase, ref, get, set, remove } from 'firebase/database';

/**
 * Utility function to migrate data from old "amsler_grids" path to new "amsler_grids_daily" path
 * This should be run only once during the migration process
 */
export const migrateGridData = async (): Promise<{
    success: boolean;
    migrated: number;
    errors: number;
    message: string;
}> => {
    const db = getDatabase();
    const oldPath = 'amsler_grids';
    const newPath = 'amsler_grids_daily';
    
    try {
        console.log(`Starting migration from ${oldPath} to ${newPath}...`);
        
        // Get all data from the old path
        const oldDataRef = ref(db, oldPath);
        const snapshot = await get(oldDataRef);
        
        if (!snapshot.exists()) {
            console.log('No data found in the old path. Migration not needed.');
            return {
                success: true,
                migrated: 0,
                errors: 0,
                message: 'No data found in the old path. Migration not needed.'
            };
        }
        
        const oldData = snapshot.val();
        const gridIds = Object.keys(oldData);
        console.log(`Found ${gridIds.length} records to migrate.`);
        
        let migratedCount = 0;
        let errorCount = 0;
        
        // Migrate each grid to the new path
        for (const gridId of gridIds) {
            try {
                const gridData = oldData[gridId];
                
                // Save to the new path
                await set(ref(db, `${newPath}/${gridId}`), gridData);
                
                console.log(`Migrated grid: ${gridId}`);
                migratedCount++;
            } catch (error) {
                console.error(`Error migrating grid ${gridId}:`, error);
                errorCount++;
            }
        }
        
        console.log(`Migration complete. Migrated: ${migratedCount}, Errors: ${errorCount}`);
        
        return {
            success: migratedCount > 0 && errorCount === 0,
            migrated: migratedCount,
            errors: errorCount,
            message: `Migration complete. Migrated: ${migratedCount}, Errors: ${errorCount}`
        };
    } catch (error) {
        console.error('Error during migration process:', error);
        return {
            success: false,
            migrated: 0,
            errors: 1,
            message: `Error during migration: ${error instanceof Error ? error.message : String(error)}`
        };
    }
};

/**
 * Utility function to delete data from the old "amsler_grids" path after successful migration
 * This should only be called after verifying the migration was successful
 */
export const deleteOldData = async (): Promise<{
    success: boolean;
    message: string;
}> => {
    const db = getDatabase();
    const oldPath = 'amsler_grids';
    
    try {
        console.log(`Deleting data from old path ${oldPath}...`);
        
        // Get all data from the old path first to confirm it exists
        const oldDataRef = ref(db, oldPath);
        const snapshot = await get(oldDataRef);
        
        if (!snapshot.exists()) {
            console.log('No data found in the old path. Nothing to delete.');
            return {
                success: true,
                message: 'No data found in the old path. Nothing to delete.'
            };
        }
        
        // Remove the entire node
        await remove(ref(db, oldPath));
        
        console.log('Successfully deleted data from old path.');
        return {
            success: true,
            message: 'Successfully deleted data from old path.'
        };
    } catch (error) {
        console.error('Error deleting old data:', error);
        return {
            success: false,
            message: `Error deleting old data: ${error instanceof Error ? error.message : String(error)}`
        };
    }
}; 